import { AlertTriangle } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { ROUTES } from "@/shared/constants/routes";
import { useCopyToClipboard } from "@/shared/hooks/useCopyToClipboard";
import { Button } from "@/shared/ui/button";
import { cn } from "@/shared/ui/cn";
import { Icon } from "@/shared/ui/icon";

interface FailureCardProps {
  title: string;
  /** What to do next, in plain words. */
  description: string;
  /**
   * **What is still working** (redesign 2026-09, pass 02 board `6a`; the same rule pass 03 puts on
   * every tenant-state escalation).
   *
   * A region that goes red on a page full of healthy regions reads as "the product is down", and the
   * owner's actual fear is that the gate has stopped letting people in. So a failure says the other
   * half out loud — "The rest of the page is live — terminals, people and doors all loaded. Only
   * this list failed." — and it leads, before the retry button, because a reader who is worried
   * stops reading at the first sentence that answers them.
   *
   * Optional only because forty-odd callers pre-date the rule; pass it wherever you know the answer,
   * and it is the first thing to add when one of those callers is next touched.
   */
  stillWorks?: string | null;
  /**
   * The one short token worth reading out to support ("EVT-503-a91f"), from `errorReference`. It is
   * the api's own request id — never invented here — and it sits beside a copy control because a
   * reader on the phone cannot retype a stack trace. Absent when the failure never reached the api.
   */
  reference?: string | null;
  onRetry?: () => void;
  retryLabel?: string;
  /** Extra way-forward actions; the overview link is always offered unless `hideHome`. */
  actions?: ReactNode;
  hideHome?: boolean;
  /** Technical detail (code, request id, stack) — collapsed by default, never the headline. */
  details?: string | null;
  /** Inline inside a list or tile rather than a full page. */
  compact?: boolean;
  className?: string;
}

/** The ONE failure card: error boundary, router error element and 404 all render it (rules/01 §4). */
export function FailureCard({
  title,
  description,
  stillWorks,
  reference,
  onRetry,
  retryLabel = "Try again",
  actions,
  hideHome = false,
  details,
  compact = false,
  className,
}: FailureCardProps) {
  const { state: copyState, copy } = useCopyToClipboard();
  return (
    <div
      role="alert"
      className={cn(
        // Board `6a`: a hairline all the way round and a 3 px danger rule down the left — the same
        // left-rule grammar an exception row uses, so "this one region is wrong" says itself the
        // same way wherever it appears.
        "flex flex-col gap-3 rounded-md border border-danger/40 border-l-[3px] border-l-danger bg-surface text-fg",
        compact ? "p-4" : "mx-auto max-w-lg p-6",
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-md bg-danger-soft text-danger-fg">
          <Icon icon={AlertTriangle} size={16} />
        </span>
        <div className="min-w-0">
          <p className={cn("font-semibold", compact ? "text-body" : "text-section")}>{title}</p>
          {/* What still works comes FIRST, above the instruction: it is the sentence the reader is
              actually asking for, and putting it under the retry button is putting it after they
              have decided how frightened to be. */}
          {stillWorks ? <p className="mt-0.5 text-cell text-fg">{stillWorks}</p> : null}
          <p className="mt-0.5 text-cell text-fg-muted">{description}</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 pl-11">
        {onRetry ? (
          <Button variant="primary" size="sm" onClick={onRetry}>
            {retryLabel}
          </Button>
        ) : null}
        {actions}
        {!hideHome ? (
          <Button asChild variant="secondary" size="sm">
            <Link to={ROUTES.overview}>Go to overview</Link>
          </Button>
        ) : null}
        {reference ? (
          // Mono and faint: it is a token to read out, not a thing to read. The copy control says
          // what happened rather than flashing a toast — a toast that has faded cannot answer "did
          // it copy?" while the reader is still on the phone.
          <span className="inline-flex items-center gap-1.5 font-mono text-caption text-fg-faint">
            {`ref ${reference}`}
            <button
              type="button"
              onClick={() => void copy(reference)}
              className="underline underline-offset-2 hover:text-fg-muted"
            >
              {copyState === "copied" ? "copied" : copyState === "failed" ? "copy failed" : "copy"}
            </button>
          </span>
        ) : null}
      </div>
      {details ? (
        <details className="pl-11 text-caption text-fg-muted">
          <summary className="cursor-pointer select-none">Technical details</summary>
          <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-sm border border-border bg-bg p-2 font-mono">
            {details}
          </pre>
        </details>
      ) : null}
    </div>
  );
}
